import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  notification,
  Select,
  DatePicker, Radio, Space,
  Tooltip, Tag
} from "antd";
import axios from "axios";
import GlobalContext from "../../utils/GlobalContext";
import mergeProps from "../../utils/MergeProps";
import LoadingModal from "../../utils/LoadingModal";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import {WarningOutlined} from "@ant-design/icons";
import { MEETING_STATUS } from "../../utils/Constants";
const { Option } = Select;
const { TimePicker } = DatePicker;
dayjs.extend(customParseFormat);
dayjs.extend(utc)

const MeetingForm = ({ open, isEdit, currentMeeting, onFinish, onCancel }) => {
  const globals = useContext(GlobalContext);
  const [form] = Form.useForm();
  const [meeting_types, setMeetingTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [periodicities, setPeriodicities] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTemplateSelected, setIsTemplateSelected] = useState(false);
  const [periodicitiesId, setPeriodicitiesId] = useState(null);
  const [duration, setDuration] = useState(null);
  const [realDuration, setRealDuration] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestInput, setGuestInput] = useState("");
  const [informatedPeople, setInformatedPeople] = useState([]);
  const [informatedPeopleInput, setInformatedPeopleInput] = useState("");

  const userEmail = globals.userEmail;
  const isOwner = currentMeeting?.creator === userEmail;
  const isCohost = currentMeeting?.co_host === userEmail;
  const isGuest = checkIsGuest(currentMeeting?.meeting_guests, userEmail);
  
  const startTimeTemp = dayjs(new Date());
  let endTimeTemp = dayjs(new Date()).add(1, 'hour');
  if (startTimeTemp.get('hour') >= 23) {
    endTimeTemp.set('hour', 23).set('minute', 59);
  }


  function checkIsGuest(meetingGuests, userEmail) {
    if (!meetingGuests) return false;

    if (typeof meetingGuests === "string") {
      return meetingGuests.split(/[,\s]+/).includes(userEmail);
    }

    if (Array.isArray(meetingGuests)) {
      return meetingGuests.some(item => item.email === userEmail && item.internal);
    }

    return false;
  }

  const fetchMeetingTypes = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-meeting-types`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setMeetingTypes(response?.data?.data);
    } catch (error) {
      console.error("Error fetching meeting types", error.response);
      notification.error({
        message: "Error obteniendo tipos de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const fetchDepartments = useCallback(async () => {
    try {
      // setLoadingClients(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-departments`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setDepartments(response?.data?.data);
    } catch (error) {
      console.error("Error fetching departments:", error.response);
      notification.error({
        message: "Error obteniendo departamentos",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const fetchTemplates = useCallback(async () => {
    try {
      // setLoadingClients(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/catalogue-templates`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setTemplates(response?.data?.data);
    } catch (error) {
      console.error("Error fetching templates:", error.response);
      notification.error({
        message: "Error obteniendo plantillas de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const fetchPeriodicities = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/catalogue-periodicities`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setPeriodicities(response?.data?.data);
    } catch (error) {
      console.error("Error fetching status:", error.response);
      notification.error({
        message: "Error obteniendo periodicidades de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const getDuration = useCallback(async (templateId) => {
    try {
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/duration-template/${templateId}`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setDuration(response?.data?.data.duration);
    } catch (error) {
      console.error("Error fetching duration:", error.response);
    }
  }, [globals.SERVICE_API_DB, globals.token]);

  const handleChangeTemplate = useCallback((value) => {
    if (!value) {
      setIsTemplateSelected(false);
      setDuration(null);
      return;
    }
    setIsTemplateSelected(true);
    const template = templates.find((template) => template.name === value);
    if (!template) {
      setIsTemplateSelected(false);
      setDuration(null);
      return;
    }
    getDuration(template.id);
    setTemplateId(template.id);
    setGuests((template.guests) ? template.guests.split(",") : []);
    form.setFieldsValue({
      title_meeting: "",
      meeting_type_name: template.meeting_type.name,
      department_name: template.department.name,
      guests: (template.guests) ? template.guests.split(",") : [], 
    });
    // Set focus on the title_meeting input after a short delay to ensure DOM is updated
    setTimeout(() => {
      const titleInput = document.querySelector('input[id^="title_meeting"]');
      if (titleInput) {
        titleInput.focus();
      }
    }, 100);
  }, [templates, form, getDuration]);

  useEffect(() => {
    form.setFieldsValue({
      periodicitiesId: 1,
      statusId: 2,
      owner: globals.userEmail,
    });

    if (isEdit) {
      if (!currentMeeting) {
        return;
      }
      const guestsT =  Array.isArray(currentMeeting.meeting_guests)
      ? currentMeeting?.meeting_guests.map(guest => guest.email.trim())
      : currentMeeting?.meeting_guests.split(',').map(guest => guest.trim());
      const informated_people = (currentMeeting.informated_people ?? "").split(",");
      handleChangeTemplate(currentMeeting.template_name);
      setGuests(guestsT);
      form.setFieldsValue({
        title_meeting: currentMeeting.title_meeting,
        template_name: currentMeeting.template_name,
        statusId: currentMeeting.statusId,
        periodicitiesId: currentMeeting.periodicitiesId,
        meeting_type_name: currentMeeting.meeting_type_name,
        department_name: currentMeeting.department_name,
        date: dayjs(currentMeeting.startDate).format("YYYY-MM-DD"),
        startTime: dayjs(currentMeeting.startDate),
        endTime: dayjs(currentMeeting.endDate),
        informated_people: informated_people,
        guests: guestsT,
        until: dayjs(new Date()).format("YYYY-MM-DD"),
        co_host: currentMeeting.co_host,
        owner: currentMeeting.creator,
      });
    }
  }, [isEdit, currentMeeting, form, open ,handleChangeTemplate, globals.userEmail]);

  useEffect(() => {
    fetchMeetingTypes();
    fetchDepartments();
    fetchTemplates();
    // fetchStatus();
    fetchPeriodicities();
  }, [fetchMeetingTypes, fetchDepartments, fetchTemplates, fetchPeriodicities]);

  const handleComplete = async (values) => {
    try {
      setLoading(true);
      //************************************************ 
      // validacion de guests e informated_people
      if (guests.length === 0) {
        notification.error({
          message: "Debe agregar al menos un invitado",
          placement: "bottomRight",
        });
        setLoading(false);
        return;
      } else {
        values.guests = guests.join(",");
      }
      if (informatedPeople.length === 0) {
        values.informated_people = null;
      } else {
        values.informated_people = informatedPeople.join(",");
      }
      // si co_host no tiene valor, se asigna un string vacio
      if (values.co_host === undefined || values.co_host === null) {
        values.co_host = "";
      }
      //validate that cohost is in guests
      if (values.co_host && !guests.includes(values.co_host)) {
        setLoading(false);
        notification.error({
          message: "El co-anfitrión debe ser un invitado",
          placement: "bottomRight",
        });
        return;
      }

      //************************************************
      // agregando campos
      values.templateId = templateId;
      values.creator = globals.userEmail;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      values.timezone = timezone;
      //************************************************
      // validacion de startDate y endDate
      // merge date with startTime
      let startDate = dayjs(values.date).set('hour', values.startTime.$H).set('minute', values.startTime.$m).set('second', 0);
      values.startDate = startDate.utc().valueOf();

      let endDate = dayjs(values.date).set('hour', values.endTime.$H).set('minute', values.endTime.$m).set('second', 0);
      values.endDate = endDate.utc().valueOf();

      if (isEdit) {

        if (values.edit_option === null || values.edit_option === undefined) {
          values.edit_option = "1";
        }

      }
      console.log("values", values);
      //validate endDate > startDate
      if (values.endDate <= values.startDate) {
        setLoading(false);
        notification.error({
          message: "La hora final debe ser mayor a la hora de inicio",
          placement: "bottomRight",
        });
        return;
      }

      //add 11:59pm to until
      if (values.until) {
        values.until = dayjs(values.until).set('hour', 23).set('minute', 59).set('second', 59).utc().valueOf();
      }

      //validate date < until
      if (values.until && dayjs(values.date).isAfter(dayjs(values.until))) {
        setLoading(false);
        notification.error({
          message: "La fecha de 'hasta' debe ser mayor a la fecha de reunión",
          placement: "bottomRight",
        });
        return;
      }

      if (isEdit) {
        values.id = currentMeeting.id;
        values.statusId = currentMeeting.statusId;
        console.log("values", values);
        const response = await axios.put(
          `${globals.SERVICE_API_DB}/v1/update-meeting/${currentMeeting.id}`,
          values,
          {
            headers: {
              "x-token": globals.token,
            },
          }
        );
        setLoading(false);
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (response?.data?.status === 0) {
          currentMeeting = mergeProps(currentMeeting, response?.data?.data);
          
          //convert guest in string
          currentMeeting.meeting_guests = currentMeeting.meeting_guests.map(guest => guest.email).join(",");
          
          onFinish({ status: "edited", newMeeting: currentMeeting });
        }
        else if (response?.data?.status === 1) {
          onFinish({ status: "bulk-edited" });
        }

        handleCancel(false);
        return;
      }
      values.statusId = MEETING_STATUS.ABIERTA;
      console.log("values", values);
      const response = await axios.post(
        `${globals.SERVICE_API_DB}/v1/create-meeting`,
        values,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setLoading(false);
      await new Promise((resolve) => setTimeout(resolve, 100));
      onFinish({ status: "created", newMeeting: response?.data?.data });
      handleCancel(false);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
      if (error.response?.data.status === -97) {
        notification.error({
          message: "No pueden editarse todas las reuniones seleccionadas ya que algunas ya han ocurrido",
          placement: "bottomRight",
        });
        return
      }
      if (error.response?.data.status === -99) {
        const msg = error.response?.data.msg;
        notification.error({
          message: `No puede eliminar al invitado ${msg} ya tiene responsabilidades en la reunión`,
          placement: "bottomRight",
          duration: 5,
        });
        return
      }

      if (error.response?.data.status === -100) {
        //const msg = error.response?.data.msg;
        notification.error({
          message: "No puede editar la fecha ya que no esta en el rango correcto",
          placement: "bottomRight",
          duration: 5,
        });
        return
      }
      if (error.response?.data.status === -101) {
        //const msg = error.response?.data.msg;
        notification.error({
          message: "El usuario no tiene el rol para editar la fecha de la reunión",
          placement: "bottomRight",
          duration: 5,
        });
        return
      }

      notification.error({
        message: "Error al crear/editar la reunión",
        placement: "bottomRight",
      });
    }
  };

  const handleCancel = (cancel = true) => {
    form.resetFields();
    setIsTemplateSelected(false);
    setPeriodicitiesId(null);
    // setEditOption(null);
    setDuration(null);
    setRealDuration(null);
    if (cancel) {
      onCancel();
    }
  };

  const filterOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  const handlePeriodicityChange = (value) => {
    setPeriodicitiesId(value);
  }

  const handleStartTimeChange = (time, timeString) => {
    if (!time) {
      return;
    }
    let endTime = time.clone().add(1, 'hour');
    const endOfDay = dayjs(time).set('hour', 23).set('minute', 59).set('second', 0);
    if (endTime.isAfter(endOfDay)) {
      endTime = dayjs(time).set('hour', 23).set('minute', 59).set('second', 0);
    }

    setRealDuration(endTime.diff(time, 'minutes'));
    form.setFieldsValue({
      endTime: endTime,
    });
  }

  const handleEndTimeChange = (time, timeString) => {
    if (!time) {
      return;
    }
    let startTime = form.getFieldValue('startTime').clone();
    startTime = startTime.set('second', 0).set('millisecond', 0);
    const endTime = time.clone();
    setRealDuration(time.diff(startTime, 'minutes'));
    form.setFieldsValue({
      endTime: endTime,
    });
    // setRealDuration(time);
  }

  const handleChangeDate = (e) => {
    const date = e.target.value;
    const startTime = form.getFieldValue('startTime');
    const newStartTime = dayjs(date).set('hour', startTime.$H).set('minute', startTime.$m).set('second', 0);
    let endTime = newStartTime.clone().add(1, 'hour');
    const endOfDay = dayjs(date).set('hour', 23).set('minute', 59).set('second', 0);
    if (endTime.isAfter(endOfDay)) {
      endTime = dayjs(date).set('hour', 23).set('minute', 59).set('second', 0);
    }

    setRealDuration(endTime.diff(newStartTime, 'minutes'));
    form.setFieldsValue({
      startTime: newStartTime,
      endTime: endTime,
    });
  }
  
  const handleEditOptionChange = (e) => {
    // setEditOption(e.target.value);
    // if (e.target.value > 1) {
    //   form.setFieldsValue({
    //     date: dayjs(currentMeeting.startDate).form at("YYYY-MM-DD"),
    //   });
    // }
  }

  return (
    <Modal
      title={isEdit ? "Editar reunión" : "Crear reunión"}
      open={open}
      onOk={handleCancel}
      onCancel={handleCancel}
      footer={null}
    >
      <LoadingModal open={loading} />
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleComplete}
        initialValues={
          {
            title_meeting: "",
            template_name: "",
            statusId: 1,
            periodicitiesId: 1,
            meeting_type_name: "",
            department_name: "",
            date: new Date().toLocaleDateString("sv-SE"),
            startTime: dayjs(new Date()),
            endTime: endTimeTemp,
            informated_people: [],
            guests: [],
            edit_option: 1,
          }
        }
      >
        <Form.Item
          name="template_name"
          label="Plantillas de reunión"
        >
          <Select
            allowClear
            showSearch
            placeholder="Selecciona una opción"
            filterOption={filterOption}
            optionFilterProp="children"
            onChange={handleChangeTemplate}
            disabled={isEdit}
          >
            {templates.map((option) => (
              <Option key={option.id} value={option.name}>
                {option.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="title_meeting"
          label="Nombre de la reunión"
          rules={[{ required: true, message: "Este campo es requerido" }]}
        >
          <Input
            disabled={!isOwner && !isCohost && isGuest} 
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="meeting_type_name"
              label="Tipo de reunión"
              rules={[{ required: true, message: "Selecciona una opción" }]}
            >
              <Select
                showSearch
                placeholder="Selecciona una opción"
                filterOption={filterOption}
                optionFilterProp="children"
                disabled={isTemplateSelected || isEdit}
              >
                {meeting_types.map((option) => (
                  <Option key={option.id} value={option.name}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="date"
              label="Fecha"
              rules={[{ required: true, message: "Este campo es requerido" }]}
            >
              <Input type="date" 
                onChange={handleChangeDate}
                disabled={!isOwner && !isCohost && isGuest}
              />
              
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="department_name"
              label="Departamento"
              rules={[{ required: true, message: "Selecciona una opción" }]}
            >
              <Select
                showSearch
                placeholder="Selecciona una opción"
                filterOption={filterOption}
                optionFilterProp="children"
                disabled={!isOwner && (isCohost || isGuest)}
              >
                {departments.map((option) => (
                  <Option key={option.id} value={option.name}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="periodicitiesId"
              label="Periodicidad"
              rules={[{ required: true, message: "Selecciona una opción" }]}
            >
              <Select
                showSearch
                placeholder="Selecciona una opción"
                filterOption={filterOption}
                optionFilterProp="children"
                onChange={handlePeriodicityChange}
                disabled={isEdit}
              >
                {periodicities.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
          <Form.Item
              name="startTime"
              label="Hora de inicio"
              rules={[{ required: true, message: "Este campo es requerido" }]}
            >
              <TimePicker 
                use12Hours 
                format={"hh:mm a"} 
                onChange={handleStartTimeChange}
                disabled={!isOwner && !isCohost && isGuest}
              />
            </Form.Item>
            <Form.Item
              name="endTime"
              label="Hora de final"
              rules={[{ required: true, message: "Este campo es requerido" }]}
            >
              <TimePicker 
                use12Hours 
                format={"hh:mm a"} 
                onChange={handleEndTimeChange}
                disabled={!isOwner && !isCohost && isGuest}
              />
            </Form.Item>
          </Col>
          <Col span={12}>

            {periodicitiesId > 1 && !isEdit && (
            <Form.Item
              name="until"
              label="Hasta"
              rules={[{ required: true, message: "Este campo es requerido" }]}
            >
              <Input type="date" />
            </Form.Item>
            )}
            {realDuration && (duration > realDuration ? (
              <Tooltip title="La duración de los puntos a tratar exceden al tiempo de la reunión">                
                <span style={{ color: "red" }}><WarningOutlined /> Duración: {realDuration} minutos</span>
                <div style={{ color: "red" }}>Duración min: {duration} minutos</div>  
              </Tooltip>
            ) : (
            <p>Duración: {realDuration} minutos</p>
            ))}
          </Col>
        </Row>
        <Form.Item
          name="owner"
          label="Anfitrión"
        >
          <Input
            disabled={true} 
          />
        </Form.Item>
        <Row gutter={16}>
        <Col span={12}>
            <Form.Item
              label="Invitados"
              tooltip="Ingrese el correo electrónico de cada invitado y presione ENTER"
              rules={[{ required: true, message: "Este campo es requerido" }]}
            >
            <Input
              placeholder="Ingrese el correo electrónico"
              value={guestInput}
              onChange={(e) => setGuestInput(e.target.value)}
              onKeyDown={(e) =>{
                if (e.key === "Enter"){
                  e.preventDefault(); 
                  if (e.target.value) {
                    //add if not exists
                    if (!guests.includes(e.target.value)){
                      setGuests([...guests, e.target.value]);
                      setGuestInput("");
                    }else{
                      notification.error({
                        message: "El usuario ya ha sido agregado",
                        placement: "bottomRight",
                      });
                    }
                  }
                }
              }}
            />
            </Form.Item>
        </Col>
        </Row>
        <Row gutter={16}>
        {guests && guests.length ? (
          guests.map((guest) => (
            <Tag
              key={guest}
              closable
              onClose={() => {
                const newGuests = guests.filter((guest2) => guest2 !== guest);
                setGuests(newGuests);
              }}
              style={{ marginBottom: 8 }}
            >
              {guest}
            </Tag>
          ))
        ) : (
          <span>No hay invitados agregados</span>
        )}
        </Row>
        <Form.Item 
          name="co_host" 
          label="Co-anfitrión"
        >
          <Select 
            placeholder="Selecciona el co-anfitrión"
            allowClear
            disabled={!isOwner && (isCohost || isGuest)}
          >
            {guests.map(guest => (
              <Select.Option key={guest} value={guest}>{guest}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item 
          label="Usuarios informados"
          tooltip="Ingrese el correo electrónico de cada usuario que al finalizar la reunión será informada y presione ENTER"
        >
          <Input
              placeholder="Ingrese el correo electrónico"
              value={informatedPeopleInput}
              onChange={(e) => setInformatedPeopleInput(e.target.value)}
              disabled={!isOwner && (isCohost || isGuest)}
              onKeyDown={(e) =>{
                if (e.key === "Enter"){
                  e.preventDefault(); 
                  if (e.target.value) {
                    //add if not exists
                    if (!informatedPeople.includes(e.target.value)){
                      setInformatedPeople([...informatedPeople, e.target.value]);
                      setInformatedPeopleInput("");
                    }
                    else {
                      notification.error({
                        message: "El usuario ya ha sido agregado",
                        placement: "bottomRight",
                      });
                    }
                  }
                }
              }}
            />
        </Form.Item>
        <Row gutter={16}>
        {informatedPeople && informatedPeople.length ? (
          informatedPeople.map((informed) => (
            <Tag
              key={informed}
              closable
              onClose={() => {
                const newInformed = informatedPeople.filter((info2) => info2 !== informed);
                setInformatedPeople(newInformed);
              }}
              style={{ marginBottom: 8 }}
            >
              {informed}
            </Tag>
          ))
        ) : (
          <span>No hay personas informadas</span>
        )}
        </Row>
        {isEdit && currentMeeting?.periodicity_group_id !== null && (
          <Form.Item
            name="edit_option"
            label="Editar reunión"
            rules={[{ required: true, message: "Selecciona una opción" }]}
          >
          <Radio.Group>
            <Space direction="vertical">
              <Radio
                onChange={handleEditOptionChange}
                value={1}
              >
                Editar solo esta reunión
              </Radio>
              <Radio
                onChange={handleEditOptionChange}
               value={2}>Editar esta y todas las reuniones futuras</Radio>
              {/*<Radio 
                onChange={handleEditOptionChange}
                value={3}>Editar todas las reuniones
              </Radio> */}
            </Space>
          </Radio.Group> 
          </Form.Item>
        )}
        <Row justify="end">
          <Col>
            <Button style={{ marginRight: 8 }} type="primary" htmlType="submit">
              {isEdit ? "Actualizar" : "Crear"}
            </Button>
            <Button style={{ marginRight: 8 }} onClick={handleCancel}>
              Cancelar
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default MeetingForm;
